import React, { createContext, useContext, useState, useEffect } from 'react';
import supabase from '../supabase/supabase';

const OrderContext = createContext();

export const useOrder = () => {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error('useOrder must be used within an OrderProvider');
  }
  return context;
};

export const OrderProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [orders, setOrders] = useState([]);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadOrders();
    
    // Set up real-time subscription for orders
    const ordersSubscription = supabase
      .channel('orders-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders'
        },
        (payload) => {
          console.log('Order change detected:', payload);
          // Reload orders when changes are detected
          loadOrders();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'order_items'
        },
        (payload) => {
          console.log('Order items change detected:', payload);
          // Reload orders when order items change
          loadOrders();
        }
      )
      .subscribe();

    return () => {
      ordersSubscription.unsubscribe();
    };
  }, []);

  const loadOrders = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          order_items(*),
          locations(name, address)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const formattedOrders = (data || []).map(order => ({
        ...order,
        id: order.id,
        orderNumber: order.order_number,
        locationId: order.location_id,
        customerInfo: order.customer_info,
        paymentInfo: order.payment_info,
        orderType: order.order_type,
        paymentStatus: order.payment_status,
        estimatedTime: order.estimated_time,
        completedAt: order.completed_at,
        createdAt: order.created_at,
        items: (order.order_items || []).map(item => ({
          ...item,
          totalPrice: parseFloat(item.total_price) || 0,
          menuItemId: item.menu_item_id,
          price: parseFloat(item.price) || 0
        }))
      }));

      console.log('Loaded orders:', formattedOrders);
      setOrders(formattedOrders);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const addToCart = (item, customizations = {}, locationId = null) => {
    const cartItem = {
      id: Date.now() + Math.random(), // Ensure unique IDs
      ...item,
      customizations,
      quantity: 1,
      totalPrice: calculateItemPrice(item, customizations),
      locationId
    };
    setCart(prev => [...prev, cartItem]);
  };

  const updateCartItem = (cartItemId, quantity) => {
    setCart(prev =>
      prev.map(item =>
        item.id === cartItemId
          ? { ...item, quantity, totalPrice: (item.totalPrice / item.quantity) * quantity }
          : item
      )
    );
  };

  const removeFromCart = (cartItemId) => {
    setCart(prev => prev.filter(item => item.id !== cartItemId));
  };

  const clearCart = () => {
    setCart([]);
  };

  const calculateItemPrice = (item, customizations) => {
    let price = parseFloat(item.price) || 0;

    // Add bread price
    if (customizations.bread && item.options?.bread) {
      const breadOption = item.options.bread.find(b => 
        (typeof b === 'string' ? b : b.name) === customizations.bread
      );
      if (breadOption && typeof breadOption === 'object' && breadOption.price) {
        price += parseFloat(breadOption.price) || 0;
      }
    }

    // Add protein price
    if (customizations.protein && item.options?.protein) {
      const proteinOption = item.options.protein.find(p =>
        (typeof p === 'string' ? p : p.name) === customizations.protein
      );
      if (proteinOption && typeof proteinOption === 'object' && proteinOption.price) {
        price += parseFloat(proteinOption.price) || 0;
      }
    }

    // Add toppings price
    if (customizations.toppings && item.options?.toppings) {
      customizations.toppings.forEach(topping => {
        const toppingOption = item.options.toppings.find(t =>
          (typeof t === 'string' ? t : t.name) === topping
        );
        if (toppingOption && typeof toppingOption === 'object' && toppingOption.price) {
          price += parseFloat(toppingOption.price) || 0;
        }
      });
    }

    // Add extras price
    if (customizations.extras && item.options?.extras) {
      customizations.extras.forEach(extra => {
        const extraOption = item.options.extras.find(e =>
          (typeof e === 'string' ? e : e.name) === extra
        );
        if (extraOption && typeof extraOption === 'object' && extraOption.price) {
          price += parseFloat(extraOption.price) || 0;
        }
      });
    }

    return Math.max(0, price);
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + item.totalPrice, 0);
  };

  const createOrder = async (customerInfo, paymentInfo, locationId) => {
    try {
      const subtotal = getCartTotal();
      const tax = subtotal * 0.08; // 8% tax
      const total = subtotal + tax;

      console.log('Creating order with:', {
        locationId,
        customerInfo,
        cart,
        subtotal,
        tax,
        total
      });

      // Create the order
      const { data: orderData, error: orderError } = await supabase
        .from('orders')
        .insert([
          {
            location_id: locationId,
            customer_info: customerInfo,
            payment_info: paymentInfo,
            status: 'pending',
            payment_status: 'paid',
            order_type: customerInfo.orderType || 'pickup',
            subtotal: subtotal,
            tax: tax,
            total: total,
            estimated_time: 15,
            priority: 'normal'
          }
        ])
        .select()
        .single();

      if (orderError) {
        console.error('Order creation error:', orderError);
        throw orderError;
      }

      console.log('Order created:', orderData);

      // Create order items
      const orderItems = cart.map(item => ({
        order_id: orderData.id,
        menu_item_id: item.id,
        name: item.name,
        price: parseFloat(item.price) || 0,
        quantity: item.quantity,
        customizations: item.customizations || {},
        total_price: item.totalPrice
      }));

      console.log('Creating order items:', orderItems);

      const { error: itemsError } = await supabase
        .from('order_items')
        .insert(orderItems);

      if (itemsError) {
        console.error('Order items creation error:', itemsError);
        throw itemsError;
      }

      const formattedOrder = {
        ...orderData,
        orderNumber: orderData.order_number,
        locationId: orderData.location_id,
        customerInfo: orderData.customer_info,
        paymentInfo: orderData.payment_info,
        orderType: orderData.order_type,
        paymentStatus: orderData.payment_status,
        estimatedTime: orderData.estimated_time,
        createdAt: orderData.created_at,
        items: cart.map(item => ({
          ...item,
          total_price: item.totalPrice
        }))
      };

      setCurrentOrder(formattedOrder);
      clearCart();
      
      // Force reload orders to ensure Kitchen Display gets updated
      setTimeout(() => {
        loadOrders();
      }, 1000);

      return {
        success: true,
        orderId: orderData.id,
        orderNumber: orderData.order_number
      };
    } catch (error) {
      console.error('Error creating order:', error);
      return {
        success: false,
        error: 'Failed to create order: ' + error.message
      };
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      const updateData = {
        status,
        updated_at: new Date().toISOString()
      };

      // Set completed_at when status changes to completed
      if (status === 'completed') {
        updateData.completed_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from('orders')
        .update(updateData)
        .eq('id', orderId);

      if (error) throw error;

      // Immediately reload orders
      await loadOrders();
    } catch (error) {
      console.error('Error updating order status:', error);
    }
  };

  const updateOrderPriority = async (orderId, priority) => {
    try {
      const { error } = await supabase
        .from('orders')
        .update({
          priority,
          updated_at: new Date().toISOString()
        })
        .eq('id', orderId);

      if (error) throw error;

      await loadOrders();
    } catch (error) {
      console.error('Error updating order priority:', error);
    }
  };

  const getOrdersByStatus = (status) => {
    return orders.filter(order => order.status === status);
  };

  const getOrdersByLocation = (locationId) => {
    return orders.filter(order => 
      order.locationId === locationId || order.location_id === locationId
    );
  };

  const getTodaysOrders = (locationId = null) => {
    const today = new Date().toDateString();
    return orders.filter(order => {
      const isToday = new Date(order.createdAt || order.created_at).toDateString() === today;
      const isLocation = locationId 
        ? (order.locationId === locationId || order.location_id === locationId) 
        : true;
      return isToday && isLocation;
    });
  };

  const getTodaysRevenue = (locationId = null) => {
    return getTodaysOrders(locationId).reduce((total, order) => 
      total + parseFloat(order.total), 0
    );
  };

  return (
    <OrderContext.Provider
      value={{
        cart,
        orders,
        currentOrder,
        isLoading,
        addToCart,
        updateCartItem,
        removeFromCart,
        clearCart,
        getCartTotal,
        createOrder,
        updateOrderStatus,
        updateOrderPriority,
        getOrdersByStatus,
        getOrdersByLocation,
        getTodaysOrders,
        getTodaysRevenue,
        calculateItemPrice,
        loadOrders
      }}
    >
      {children}
    </OrderContext.Provider>
  );
};